import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { heroMovies } from '@/mocks/movies';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((index: number) => {
    setCurrent(index);
  }, []);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroMovies.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + heroMovies.length) % heroMovies.length);
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(next, 5000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [next]);

  const movie = heroMovies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={movie.coverImage}
          alt={movie.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
      </div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-white whitespace-nowrap">
                  {movie.category}
                </span>
                <span className="text-sm text-white/80">{movie.year}</span>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                  </div>
                  <span className="text-sm text-accent-500 font-semibold">{movie.rating}</span>
                </div>
              </div>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                {movie.title}
              </h2>
              <p className="text-sm md:text-base text-white/70 mb-6 line-clamp-2 leading-relaxed">
                {movie.description}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/article/${movie.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-fill text-base"></i>
                  </div>
                  立即观看
                </Link>
                <Link
                  to={`/article/${movie.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap border border-white/30"
                >
                  查看详情
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              index === current
                ? 'bg-white w-6'
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`切换到第${index + 1}个`}
          />
        ))}
      </div>
    </section>
  );
}